import React, { useState, useEffect } from 'react';
import { List, Avatar, Form, Button, Input, Popconfirm, message, Card, Typography, Space, Divider, Select } from 'antd';
import { DeleteOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import { userStore } from '../../store/userStore';
import {
    useAddMemoComment,
    useDeleteMemoComment,
    useUpdateMemoComment
} from '../../hooks/memo/memoQuery';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { Option } = Select;

// Available roles for receiving comments
const RECEIVER_ROLES = [
    { label: 'Head of Unit', value: 'HEAD_OF_UNIT' },
    { label: 'Head of Department', value: 'HEAD_OF_DEPARTMENT' },
    { label: 'Vice Chancellor', value: 'VC' },
    { label: 'Bursar', value: 'BURSAR' }
];

// Component for editing an existing comment
const CommentEditor = ({ content, receiverRole, onSubmit, onCancel }) => {
    const [value, setValue] = useState(content);
    const [receiver, setReceiver] = useState(receiverRole);

    const handleChange = (e) => {
        setValue(e.target.value);
    };

    const handleReceiverChange = (value) => {
        setReceiver(value);
    };

    const handleSubmit = () => {
        if (!value.trim()) {
            message.error('Comment cannot be empty');
            return;
        }
        onSubmit(value, receiver);
    };

    return (
        <div>
            <Form.Item>
                <TextArea rows={4} onChange={handleChange} value={value} />
            </Form.Item>
            {/*<Form.Item label="Send to (optional)">*/}
            {/*    <Select*/}
            {/*        value={receiver}*/}
            {/*        onChange={handleReceiverChange}*/}
            {/*        style={{ width: 200 }}*/}
            {/*    >*/}
            {/*        {RECEIVER_ROLES.map(role => (*/}
            {/*            <Option key={role.value} value={role.value}>{role.label}</Option>*/}
            {/*        ))}*/}
            {/*    </Select>*/}
            {/*</Form.Item>*/}
            <Form.Item>
                <Space>
                    <Button htmlType="submit" type="primary" onClick={handleSubmit} style={{ marginRight: 8 }}>
                        Save
                    </Button>
                    <Button onClick={onCancel}>Cancel</Button>
                </Space>
            </Form.Item>
        </div>
    );
};

// Component for adding a new comment
const CommentForm = ({ memoId, onCommentAdded, nextApprovalRole }) => {
    const [value, setValue] = useState('');
    const [receiver, setReceiver] = useState(nextApprovalRole || '');
    const [submitting, setSubmitting] = useState(false);
    const user = userStore(state => state.user);
    const addMemoComment = useAddMemoComment();

    // Set default receiver based on next approval role
    useEffect(() => {
        if (nextApprovalRole && !receiver) {
            setReceiver(nextApprovalRole);
        }
    }, [nextApprovalRole, receiver]);

    const handleChange = (e) => {
        setValue(e.target.value);
    };

    const handleReceiverChange = (value) => {
        setReceiver(value);
    };

    const handleSubmit = async () => {
        if (!value.trim()) {
            message.error('Comment cannot be empty');
            return;
        }

        // if (!receiver) {
        //     message.error('Please select a receiver for the comment');
        //     return;
        // }

        setSubmitting(true);
        try {
            const payload = {
                memo: memoId,
                content: value,
                to: receiver
            };

            const result = await addMemoComment(payload);
            setValue('');
            message.success('Comment added successfully');
            onCommentAdded(result);
        } catch (error) {
            console.error('Error adding comment:', error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            <Form.Item>
                <TextArea rows={4} onChange={handleChange} value={value} placeholder="Add a comment..." />
            </Form.Item>
            {/*<Form.Item label="Send to (optional)">*/}
            {/*    <Select*/}
            {/*        value={receiver}*/}
            {/*        onChange={handleReceiverChange}*/}
            {/*        style={{ width: 200 }}*/}
            {/*        placeholder="Select receiver"*/}
            {/*    >*/}
            {/*        {RECEIVER_ROLES.map(role => (*/}
            {/*            <Option key={role.value} value={role.value}>{role.label}</Option>*/}
            {/*        ))}*/}
            {/*    </Select>*/}
            {/*</Form.Item>*/}
            <Form.Item>
                <Button htmlType="submit" loading={submitting} onClick={handleSubmit} type="primary">
                    Add Comment
                </Button>
            </Form.Item>
        </div>
    );
};

// Custom comment component
const CommentItem = ({ comment, onEdit, onDelete, canEditDelete }) => {
    // Format role labels for display
    const getRoleLabel = (role) => {
        const foundRole = RECEIVER_ROLES.find(r => r.value === role);
        return foundRole ? foundRole.label : role;
    };

    return (
        <Card
            style={{ marginBottom: '16px' }}
            size="small"
            actions={
                canEditDelete ? [
                    <Button
                        onClick={() => onEdit(comment.id)}
                        type="text"
                        icon={<EditOutlined />}
                    >
                        Edit
                    </Button>,
                    // <Popconfirm
                    //     title="Delete this comment"
                    //     description="Are you sure you want to delete this comment?"
                    //     onConfirm={() => onDelete(comment.id)}
                    //     okText="Yes"
                    //     cancelText="No"
                    // >
                    //     <Button
                    //         type="text"
                    //         danger
                    //         icon={<DeleteOutlined />}
                    //     >
                    //         Delete
                    //     </Button>
                    // </Popconfirm>
                ] : []
            }
        >
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <Avatar icon={<UserOutlined />} style={{ marginRight: '12px' }} />
                <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '4px' }}>
                        <Text strong>
                            {/*{getRoleLabel(comment.sender)} → {comment.receiver ? getRoleLabel(comment.receiver) : "General"}*/}
                            {comment.sentBy?.name} → {comment.receiver ? getRoleLabel(comment.receiver) : "General"}
                        </Text>
                        <Text type="secondary" style={{ marginLeft: '8px' }}>{comment.dateString}</Text>
                    </div>
                    <div>
                        <Text>{comment.content}</Text>
                    </div>
                </div>
            </div>
        </Card>
    );
};

// Main component for comments list and management
const MemoComment = ({ memo, comments, onCommentsChange }) => {
    const [editingCommentId, setEditingCommentId] = useState(null);
    const user = userStore(state => state.user);
    const deleteMemoComment = useDeleteMemoComment();
    const updateMemoComment = useUpdateMemoComment();

    const handleDelete = async (commentId) => {
        try {
            await deleteMemoComment(commentId);
            message.success('Comment deleted successfully');
            // Remove the deleted comment from the state
            onCommentsChange(comments.filter(c => c.id !== commentId));
        } catch (error) {
            console.error('Error deleting comment:', error);
        }
    };

    const handleEdit = (commentId) => {
        setEditingCommentId(commentId);
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
    };

    const handleUpdate = async (commentId, content, receiverRole) => {
        try {
            const comment = comments.find(c => c.id === commentId);
            const payload = {
                memo: memo.id,
                commentId: commentId,
                content: content,
                to: receiverRole // Use the selected receiver role
            };

            const updatedComment = await updateMemoComment(payload);
            message.success('Comment updated successfully');

            // Update the comment in the state
            onCommentsChange(
                comments.map(c => c.id === commentId ? updatedComment : c)
            );

            setEditingCommentId(null);
        } catch (error) {
            console.error('Error updating comment:', error);
        }
    };

    const handleCommentAdded = (newComment) => {
        onCommentsChange([...comments, newComment]);
    };

    // Check if user is authorized to edit or delete a comment based on sender role
    const canEditDelete = (comment) => {
        // User can edit/delete if they created the comment (i.e., their role matches the sender role)
        return user.id === comment.sentBy?.id
    };

    return (
        <div className="memo-comments">
            <Title level={4}>Comments</Title>
            <Divider style={{ margin: '12px 0' }} />

            {comments.length > 0 ? (
                <List
                    dataSource={comments}
                    header={`${comments.length} ${comments.length > 1 ? 'comments' : 'comment'}`}
                    itemLayout="vertical"
                    renderItem={comment => (
                        editingCommentId === comment.id ? (
                            <CommentEditor
                                content={comment.content}
                                receiverRole={comment.receiver}
                                onSubmit={(content, receiver) => handleUpdate(comment.id, content, receiver)}
                                onCancel={handleCancelEdit}
                            />
                        ) : (
                            <CommentItem
                                comment={comment}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                canEditDelete={canEditDelete(comment)}
                            />
                        )
                    )}
                />
            ) : (
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <Text type="secondary">No comments yet</Text>
                </div>
            )}

            <Divider style={{ margin: '20px 0' }} />
            {!(memo.isAwaitingCommentRequest && memo.awaitingCommentFromUser?.userId === user.userId) && <>
                <Title level={5}>Add a new comment</Title>
                <CommentForm
                    memoId={memo.id}
                    onCommentAdded={handleCommentAdded}
                    nextApprovalRole={memo.nextApprovalRole}
                />
            </>}
        </div>
    );
};

export default MemoComment;
